/*
 * Lemonade Training Oy — all rights reserved.
 * Body & Demographics card (LT-479).
 *
 * Surfaces the four optional User fields the radar scoring algorithm consumes:
 * gender, height, bodyWeight, birthYear. All inputs are strictly optional —
 * empty input writes `null` to Firestore (NEVER 0), which the scoring engine's
 * lookupMultiplier path resolves to a neutral 1.0 multiplier (SCORING-V1.md
 * §4.4). The tooltip copy (AC6) makes optionality + the accuracy upside
 * explicit so athletes don't feel forced to disclose.
 *
 * Default gender is "prefer_not_to_say" — explicit non-disclosure is honest
 * for GDPR audit trails (vs writing nothing) but behaves identically to undisclosed
 * in scoring.
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { doc, serverTimestamp, updateDoc } from "firebase/firestore";
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@lemonadetraining/web-ui";
import { IconDeviceFloppy, IconInfoCircle } from "@tabler/icons-react";
import {
  COLLECTIONS,
  firebaseDB,
  notifyActions,
  NOTIFICATION_TYPE,
} from "@lemonadetraining/web-shared";

type Gender = "male" | "female" | "other" | "prefer_not_to_say";

const GENDER_OPTIONS: { value: Gender; label: string }[] = [
  { value: "male", label: "Male" },
  { value: "female", label: "Female" },
  { value: "other", label: "Other" },
  { value: "prefer_not_to_say", label: "Prefer not to say" },
];

const HEIGHT_MIN = 100;
const HEIGHT_MAX = 250;
const WEIGHT_MIN = 30;
const WEIGHT_MAX = 300;
const BIRTH_YEAR_MIN = 1925;

const TOOLTIP_COPY =
  "Sharing your gender and bodyweight helps us tailor your radar comparison so a 100kg back squat is judged against athletes in your bracket — not the general adult average. It's strictly optional. If you skip these, your scores compare against a unisex \"General adult\" baseline. You can change or remove them anytime. Birth year is reserved for a future scoring upgrade. Height is for your profile only — it doesn't affect your radar.";

interface UserDemographics {
  gender?: Gender | null;
  height?: number | null;
  bodyWeight?: number | null;
  birthYear?: number | null;
}

interface RootState {
  user: {
    user: { uid: string } | null;
    userInfoData?: UserDemographics;
  };
}

// Stable empty-stored reference so the hydration effect doesn't fire every
// render when userInfoData is undefined (a fresh `?? {}` literal would create
// a new object identity on each render and trip the dependency check).
const EMPTY_DEMOGRAPHICS: UserDemographics = Object.freeze({});

/**
 * Convert a free-form text input to either a positive number or null. Empty
 * strings, whitespace, and non-numeric text all collapse to null so the
 * Firestore write never accidentally stores `0` as a "disclosed" value.
 */
function parseNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0 ? n : null;
}

interface FieldErrors {
  height?: string;
  bodyWeight?: string;
  birthYear?: string;
}

function validate(values: { height: string; bodyWeight: string; birthYear: string }): FieldErrors {
  const errors: FieldErrors = {};
  const currentYear = new Date().getFullYear();

  if (values.height.trim() !== "") {
    const h = Number(values.height);
    if (!Number.isFinite(h) || h < HEIGHT_MIN || h > HEIGHT_MAX) {
      errors.height = `Height must be between ${HEIGHT_MIN} and ${HEIGHT_MAX} cm.`;
    }
  }
  if (values.bodyWeight.trim() !== "") {
    const w = Number(values.bodyWeight);
    if (!Number.isFinite(w) || w < WEIGHT_MIN || w > WEIGHT_MAX) {
      errors.bodyWeight = `Bodyweight must be between ${WEIGHT_MIN} and ${WEIGHT_MAX} kg.`;
    }
  }
  if (values.birthYear.trim() !== "") {
    const y = Number(values.birthYear);
    if (!Number.isFinite(y) || y < BIRTH_YEAR_MIN || y > currentYear) {
      errors.birthYear = `Birth year must be between ${BIRTH_YEAR_MIN} and ${currentYear}.`;
    }
  }
  return errors;
}

const InfoTooltip: React.FC<{ id: string }> = ({ id }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label="Why we ask about body and demographics"
        aria-describedby={id}
        className="text-muted-foreground hover:text-foreground inline-flex"
        data-testid={`demographics-info-${id}`}
      >
        <IconInfoCircle size={16} aria-hidden="true" />
      </button>
    </TooltipTrigger>
    <TooltipContent id={id} className="max-w-xs text-left">
      {TOOLTIP_COPY}
    </TooltipContent>
  </Tooltip>
);

const BodyDemographicsCard: React.FC = () => {
  const dispatch = useDispatch();
  const uid = useSelector((state: RootState) => state.user.user?.uid ?? null);
  // Subscribe to userInfoData by reference — Redux returns a stable reference
  // until the slice replaces it, so the hydration effect fires once per real
  // change rather than per render.
  const stored = useSelector(
    (state: RootState): UserDemographics => state.user.userInfoData ?? EMPTY_DEMOGRAPHICS
  );

  // Default gender to prefer_not_to_say so the form respects privacy out of
  // the box; pre-fill from Firestore if the athlete already chose something.
  const initialGender: Gender = (stored.gender as Gender) ?? "prefer_not_to_say";
  const [gender, setGender] = useState<Gender>(initialGender);
  const [height, setHeight] = useState<string>(
    typeof stored.height === "number" ? String(stored.height) : ""
  );
  const [bodyWeight, setBodyWeight] = useState<string>(
    typeof stored.bodyWeight === "number" ? String(stored.bodyWeight) : ""
  );
  const [birthYear, setBirthYear] = useState<string>(
    typeof stored.birthYear === "number" ? String(stored.birthYear) : ""
  );
  const [submitting, setSubmitting] = useState(false);

  // Re-sync local form state when the Firestore-backed Redux slice swaps in a
  // new userInfoData object (e.g. another tab saves). Reference compare relies
  // on Redux producing a fresh reference only on real updates.
  const [hydratedFor, setHydratedFor] = useState<UserDemographics | null>(null);
  useEffect(() => {
    if (hydratedFor === stored) return;
    setGender((stored.gender as Gender) ?? "prefer_not_to_say");
    setHeight(typeof stored.height === "number" ? String(stored.height) : "");
    setBodyWeight(typeof stored.bodyWeight === "number" ? String(stored.bodyWeight) : "");
    setBirthYear(typeof stored.birthYear === "number" ? String(stored.birthYear) : "");
    setHydratedFor(stored);
  }, [stored, hydratedFor]);

  const errors = validate({ height, bodyWeight, birthYear });
  const hasErrors = Object.keys(errors).length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hasErrors || !uid) return;

    const parsedBodyWeight = parseNumberOrNull(bodyWeight);
    const previousBodyWeight = typeof stored.bodyWeight === "number" ? stored.bodyWeight : null;
    const bodyWeightChanged = parsedBodyWeight !== previousBodyWeight;

    // AC9: empty input → null, never 0. The radar scoring engine's
    // lookupMultiplier path treats null/undefined as multiplier 1.0
    // (SCORING-V1 §4.4); writing 0 here would corrupt that contract.
    const payload: Record<string, unknown> = {
      gender,
      height: parseNumberOrNull(height),
      bodyWeight: parsedBodyWeight,
      birthYear: parseNumberOrNull(birthYear),
    };
    // bodyWeightUpdatedAt is only touched when the value actually changes —
    // saving the form without a weight edit must not bump the staleness
    // timestamp and trigger a follow-up "is this still accurate?" prompt.
    if (bodyWeightChanged) {
      payload.bodyWeightUpdatedAt = serverTimestamp();
    }

    setSubmitting(true);
    try {
      await updateDoc(doc(firebaseDB, COLLECTIONS.Users, uid), payload);
      // The form's controlled state is already up-to-date; Redux's userInfoData
      // refreshes on next ProfilePage mount via the existing loadUserInfoData
      // path (no re-dispatch needed for the success toast).
      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.success,
          message: "Body & demographics updated.",
        })
      );
    } catch (error) {
      console.error("Failed to update demographics:", error);
      dispatch(
        notifyActions.sendNotification({
          type: NOTIFICATION_TYPE.error,
          message: "Failed to update. Please try again.",
        })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <TooltipProvider>
      <form
        className="flex flex-col gap-4"
        onSubmit={handleSubmit}
        data-testid="body-demographics-form"
      >
        <p className="text-sm text-muted-foreground">
          All fields are optional. Sharing them tunes your radar comparison; leaving them empty
          compares you against a neutral &quot;General adult&quot; baseline.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Gender */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="demographics-gender">Gender</Label>
              <InfoTooltip id="demographics-gender-tooltip" />
            </div>
            <Select value={gender} onValueChange={(v) => setGender(v as Gender)}>
              <SelectTrigger id="demographics-gender" data-testid="demographics-gender-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {GENDER_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Birth year */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="demographics-birth-year">Birth year</Label>
              <InfoTooltip id="demographics-birth-year-tooltip" />
            </div>
            <Input
              id="demographics-birth-year"
              type="number"
              inputMode="numeric"
              min={BIRTH_YEAR_MIN}
              max={new Date().getFullYear()}
              placeholder="e.g. 1990"
              value={birthYear}
              onChange={(e) => setBirthYear(e.target.value)}
              aria-invalid={errors.birthYear ? "true" : undefined}
              aria-describedby={errors.birthYear ? "demographics-birth-year-error" : undefined}
              data-testid="demographics-birth-year"
            />
            {errors.birthYear ? (
              <p
                id="demographics-birth-year-error"
                role="alert"
                className="text-xs text-destructive"
              >
                {errors.birthYear}
              </p>
            ) : null}
          </div>

          {/* Bodyweight */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="demographics-bodyweight">Bodyweight (kg)</Label>
              <InfoTooltip id="demographics-bodyweight-tooltip" />
            </div>
            <Input
              id="demographics-bodyweight"
              type="number"
              inputMode="decimal"
              step="any"
              min={WEIGHT_MIN}
              max={WEIGHT_MAX}
              placeholder="e.g. 75"
              value={bodyWeight}
              onChange={(e) => setBodyWeight(e.target.value)}
              aria-invalid={errors.bodyWeight ? "true" : undefined}
              aria-describedby={errors.bodyWeight ? "demographics-bodyweight-error" : undefined}
              data-testid="demographics-bodyweight"
            />
            {errors.bodyWeight ? (
              <p
                id="demographics-bodyweight-error"
                role="alert"
                className="text-xs text-destructive"
              >
                {errors.bodyWeight}
              </p>
            ) : null}
          </div>

          {/* Height */}
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="demographics-height">Height (cm)</Label>
              <InfoTooltip id="demographics-height-tooltip" />
            </div>
            <Input
              id="demographics-height"
              type="number"
              inputMode="numeric"
              min={HEIGHT_MIN}
              max={HEIGHT_MAX}
              placeholder="e.g. 175"
              value={height}
              onChange={(e) => setHeight(e.target.value)}
              aria-invalid={errors.height ? "true" : undefined}
              aria-describedby={errors.height ? "demographics-height-error" : undefined}
              data-testid="demographics-height"
            />
            {errors.height ? (
              <p id="demographics-height-error" role="alert" className="text-xs text-destructive">
                {errors.height}
              </p>
            ) : null}
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="outline"
            disabled={hasErrors || submitting || !uid}
            data-testid="demographics-save"
          >
            {submitting ? (
              <Spinner className="h-4 w-4 mr-2" />
            ) : (
              <IconDeviceFloppy className="h-4 w-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </form>
    </TooltipProvider>
  );
};

export default BodyDemographicsCard;
